import React, { useEffect, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { useAppSelector } from 'app/hooks';

export const TOP_PROCESS_BAR_HEIGHT = 80;
const REVIEWED_LABEL = 'Reviewed';
const LEGACY_REVIEWED_LABEL = 'Reviewed & Complete!';

export interface StepProcessProps {
  title: string;
  percentOnProcess: number;
  specialId?: string;
  maxWidth?: number;
  /** Hidden until its section is actually rendered, for example after a Yes answer. */
  optional?: boolean;
}

interface Props {
  icon?: any;
  title?: string;
  stepProcess: Array<StepProcessProps>;
  process: number;
  barHeight?: number;
  isMobile?: boolean;
}

export function sectionDomId(process: StepProcessProps) {
  return process.specialId ?? process.title.replace(/ /g, '').replace('/', '');
}

function isSectionOnPage(id: string) {
  const el = document.getElementById(id);
  return !!el && el.getClientRects().length > 0;
}

function findReviewedCheckbox() {
  const reviewedLabel = Array.from(document.querySelectorAll('span')).find((node) => {
    const text = node.textContent?.trim();
    if (text !== LEGACY_REVIEWED_LABEL && text !== REVIEWED_LABEL) return false;
    if (node.closest('nav[aria-label="Section steps"]')) return false;
    return !!node.parentElement?.querySelector('input[type="checkbox"]');
  });
  return reviewedLabel?.parentElement?.querySelector<HTMLInputElement>('input[type="checkbox"]') ?? null;
}

function stepSignature(steps: StepProcessProps[]) {
  return steps.map((step) => sectionDomId(step)).join('|');
}

const TopProcessBar = (props: Props) => {
  const [activeStep, setActiveStep] = useState(0);
  const [pageReviewed, setPageReviewed] = useState(false);
  const isMobile = props.isMobile || window.innerWidth <= 768;
  const sidebarCollapsed = useAppSelector((store) => store.appStore.sidebarCollapsed);
  const [steps, setSteps] = useState<StepProcessProps[]>([]);
  const definedStepsRef = useRef(props.stepProcess);
  definedStepsRef.current = props.stepProcess;
  const pinnedIndexRef = useRef<number | null>(null);
  const pinTimerRef = useRef<number | null>(null);
  const showStepper = steps.length >= 2;
  const stepKey = props.stepProcess.map((step) => step.specialId || step.title).join('|');

  const sidebarWidth = sidebarCollapsed ? 130 : 290;
  const topProcessBarWidth = isMobile ? '100vw' : `calc(100vw - ${sidebarWidth}px)`;

  useEffect(() => {
    if (definedStepsRef.current.length < 2) {
      setSteps([]);
      return;
    }

    const syncVisibleSteps = () => {
      const defined = definedStepsRef.current;
      const visible = defined.filter(
        (step) => !step.optional || isSectionOnPage(sectionDomId(step)),
      );
      if (visible.length === 0) {
        setSteps((current) => (current.length === 0 ? current : []));
        return;
      }

      const reviewedLabel = Array.from(document.querySelectorAll('span')).find((node) => {
        const text = node.textContent?.trim();
        if (text !== LEGACY_REVIEWED_LABEL && text !== REVIEWED_LABEL) return false;
        if (node.closest('nav[aria-label="Section steps"]')) return false;
        return !!node.parentElement?.querySelector('input[type="checkbox"]');
      });
      const anchor = reviewedLabel?.parentElement;
      if (anchor && anchor.id !== 'Reviewed' && !document.getElementById('Reviewed')) {
        anchor.id = 'Reviewed';
      }

      const next = [
        ...visible,
        { title: REVIEWED_LABEL, percentOnProcess: 100, specialId: 'Reviewed', optional: false },
      ];
      setSteps((current) => (stepSignature(current) === stepSignature(next) ? current : next));
      setActiveStep((current) => Math.min(current, next.length - 1));
    };

    syncVisibleSteps();
    const root = document.querySelector('.main-container') ?? document.body;
    const pageObserver = new MutationObserver(syncVisibleSteps);
    pageObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    return () => pageObserver.disconnect();
  }, [stepKey]);

  useEffect(() => {
    if (!showStepper) {
      setPageReviewed(false);
      return;
    }

    const syncReviewed = () => {
      const checked = !!findReviewedCheckbox()?.checked;
      setPageReviewed((current) => (current === checked ? current : checked));
    };

    syncReviewed();
    const root = document.querySelector('.main-container') ?? document.body;
    const onClick = () => window.setTimeout(syncReviewed, 0);
    root.addEventListener('click', onClick);
    const reviewedObserver = new MutationObserver(syncReviewed);
    reviewedObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['checked', 'disabled'],
    });
    return () => {
      root.removeEventListener('click', onClick);
      reviewedObserver.disconnect();
    };
  }, [showStepper, steps]);

  useEffect(() => {
    if (!showStepper) return;

    const targets = steps
      .map((step, index) => ({ index, el: document.getElementById(sectionDomId(step)) }))
      .filter((item): item is { index: number; el: HTMLElement } => item.el != null);
    if (targets.length === 0) return;

    const scroller = document.querySelector('.content-styled') as HTMLElement | null;
    if (!scroller) return;

    const updateActiveStep = () => {
      if (pinnedIndexRef.current != null) {
        if (pinTimerRef.current != null) window.clearTimeout(pinTimerRef.current);
        pinTimerRef.current = window.setTimeout(() => {
          pinnedIndexRef.current = null;
          pinTimerRef.current = null;
        }, 160);
        return;
      }

      const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      const canScroll = scroller.scrollHeight > scroller.clientHeight + 8;
      if (canScroll && distanceFromBottom <= 48) {
        setActiveStep(steps.length - 1);
        return;
      }

      const line = scroller.getBoundingClientRect().top + scroller.clientHeight * 0.45;
      let current = targets[0].index;
      for (const item of targets) {
        if (item.el.getBoundingClientRect().top <= line) current = item.index;
      }
      setActiveStep(current);
    };

    updateActiveStep();
    scroller.addEventListener('scroll', updateActiveStep, { passive: true });
    window.addEventListener('resize', updateActiveStep);
    return () => {
      scroller.removeEventListener('scroll', updateActiveStep);
      window.removeEventListener('resize', updateActiveStep);
    };
  }, [showStepper, steps]);

  const holdStepPin = () => {
    if (pinTimerRef.current != null) window.clearTimeout(pinTimerRef.current);
    pinTimerRef.current = window.setTimeout(() => {
      pinnedIndexRef.current = null;
      pinTimerRef.current = null;
    }, 160);
  };

  const handleStepClick = (index: number, process: StepProcessProps) => {
    pinnedIndexRef.current = index;
    setActiveStep(index);
    holdStepPin();

    const scroller = document.querySelector('.content-styled') as HTMLElement | null;
    if (index === steps.length - 1 && scroller) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
      return;
    }

    const el = document.getElementById(sectionDomId(process));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div
      style={{
        zIndex: isMobile ? 998 : 999,
        position: 'fixed',
        width: topProcessBarWidth,
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
        padding: 0,
        top: isMobile ? '60px' : undefined,
        left: isMobile ? '0' : undefined,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: isMobile ? '0 0 12px 12px' : '0 0 20px 20px',
          border: '1px solid #eee',
          padding: isMobile ? '0 16px 10px 16px' : '0 28px 14px 28px',
          margin: 0,
          boxShadow: isMobile ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        <div
          className="w-100 d-flex align-items-center"
          style={{
            minHeight: isMobile ? 32 : 40,
            padding: isMobile ? '8px 0 0 0' : '10px 0 0 0',
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: isMobile ? 12 : 14,
              background: 'linear-gradient(90deg, #4d88f0 0%, #8cb5f3 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: showStepper ? 0 : isMobile ? 10 : 15,
              display: '-webkit-box',
              WebkitLineClamp: isMobile ? 1 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              maxWidth: isMobile ? '100%' : undefined,
            }}
          >
            {props.title}
          </span>
        </div>

        {showStepper && (
          <nav aria-label="Section steps" style={{ marginTop: 8, marginBottom: isMobile ? 8 : 12 }}>
            <ol
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                listStyle: 'none',
                margin: 0,
                padding: '6px 0 4px',
                overflowX: 'auto',
              }}
            >
              {steps.map((process, index) => {
                const state = pageReviewed || index < activeStep ? 'complete' : index === activeStep ? 'active' : 'future';
                return (
                  <li key={`${process.title}-${index}`} style={{ display: 'flex', alignItems: 'center' }}>
                    {index > 0 && (
                      <span aria-hidden="true" style={{ margin: '0 16px', color: '#cbd5e1', fontSize: 16 }}>
                        →
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleStepClick(index, process)}
                      aria-current={state === 'active' ? 'step' : undefined}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        border: 0,
                        background: state === 'active' ? '#eef4fe' : 'transparent',
                        borderRadius: 8,
                        padding: '6px 10px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          background: state === 'complete' ? '#d1fae5' : state === 'active' ? '#3e73f0' : '#fff',
                          border: state === 'future' ? '1.5px solid #cbd5e1' : '1.5px solid transparent',
                          color: state === 'complete' ? '#047857' : '#fff',
                        }}
                      >
                        {state === 'complete' ? (
                          <CheckIcon style={{ fontSize: 14 }} />
                        ) : (
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              background: state === 'active' ? '#fff' : 'transparent',
                            }}
                          />
                        )}
                      </span>
                      <span
                        style={{
                          fontSize: isMobile ? 12 : 14,
                          fontWeight: state === 'active' ? 700 : 500,
                          color: state === 'active' ? '#1e3a8a' : '#64748b',
                        }}
                      >
                        {process.title}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>
        )}
      </div>
    </div>
  );
};

export default TopProcessBar;
